import { AnimalTransporterStatus } from 'src/app/modules/animal-transporter/data/enums/animal-transporter-status.enum';
import { ClsfActivityFeature } from 'src/app/modules/classifiers/data/enums/clsf-activity-feature.enum';
import { ClsfLegislationType } from 'src/app/modules/classifiers/data/enums/clsf-legislation-type.enum';
import { ClsfSanctionCreateSurface } from 'src/app/modules/classifiers/data/enums/clsf-sanction-create-surface.enum';
import { SgpSpeciesDeclarationKind } from 'src/app/modules/classifiers/data/enums/sgp-species-declaration-kind.enum';
import { AttachedDocumentType } from 'src/app/modules/control-object/data/dto/enums/attached-document-type.enum';
import { MbmOriginRegion } from 'src/app/modules/control-object/data/dto/enums/mbm-origin-region.enum';
import { SgpAnimalSpecies } from 'src/app/modules/control-object/data/dto/enums/sgp-animal-species.enum';
import { SgpCategory } from 'src/app/modules/control-object/data/dto/enums/sgp-category.enum';
import { IncidentAction } from 'src/app/modules/inspection/data/enums/incident-action.enum';
import { InspectionProgressStep } from 'src/app/modules/inspection/data/enums/inspection-progress-step.enum';
import { QuestionnaireStatus } from 'src/app/modules/inspection/data/enums/questionnaire-status.enum';
import { ReportRecommendation } from 'src/app/modules/report/data/enums/report-recommendation.enum';
import { SanctionAppealCourtOutcome } from 'src/app/modules/sanction/data/enums/sanction-appeal-court-outcome.enum';
import { SanctionChildActionKind } from 'src/app/modules/sanction/data/enums/sanction-child-action-kind.enum';
import { SanctionFieldCode } from 'src/app/modules/sanction/data/enums/sanction-field-code.enum';
import { ActivityStatus } from 'src/app/shared/data/dto/enums/activity-status.enum';
import { ClsfActivityRegistrationType } from 'src/app/shared/data/dto/enums/clsf-activity-registration-type.enum';
import { ControlObjectType } from 'src/app/shared/data/dto/enums/control-object-type.enum';
import { DataSource } from 'src/app/shared/data/dto/enums/data-source.enum';
import { EvaluationRule } from 'src/app/shared/data/dto/enums/evaluation-rule.enum';
import { EventPriority } from 'src/app/shared/data/dto/enums/event-priority.enum';
import { EventStatus } from 'src/app/shared/data/dto/enums/event-status.enum';
import { ImplementationOfDeficiencyPlan } from 'src/app/shared/data/dto/enums/implementation-of-deficiency-plan.enum';
import { InspectionKind } from 'src/app/shared/data/dto/enums/inspection-kind.enum';
import { InspectionSubType } from 'src/app/shared/data/dto/enums/inspection-sub-type.enum';
import { InspectionType } from 'src/app/shared/data/dto/enums/inspection-type.enum';
import { Month } from 'src/app/shared/data/dto/enums/month.enum';
import { OrderStatus } from 'src/app/shared/data/dto/enums/order-status.enum';
import { ReportInvestigationMethod } from 'src/app/shared/data/dto/enums/report-investigation-method.enum';
import { ReportStatus } from 'src/app/shared/data/dto/enums/report-status.enum';
import { ReportType } from 'src/app/shared/data/dto/enums/report-type.enum';
import { RequirementResult } from 'src/app/shared/data/dto/enums/requirement-result.enum';
import { RequirementRiskWeight } from 'src/app/shared/data/dto/enums/requirement-risk-weight.enum';
import { RiskGroup } from 'src/app/shared/data/dto/enums/risk-group.enum';
import { SanctionAppealState } from 'src/app/shared/data/dto/enums/sanction-appeal-state.enum';
import { SanctionStatus } from 'src/app/shared/data/dto/enums/sanction-status.enum';
import { SelfControlSystem } from 'src/app/shared/data/dto/enums/self-control-system.enum';
import { SubjectLegalForm } from 'src/app/shared/data/dto/enums/subject-legal-form.enum';
import { SubjectStatus } from 'src/app/shared/data/dto/enums/subject-status.enum';
import { SubjectType } from 'src/app/shared/data/dto/enums/subject-type.enum';
import { ActivityIndicatorStatus } from 'src/app/shared/data/enums/activity-indicator-status.enum';

/**
 * The TS mirror of every backend catalogue enum, keyed by the backend enum name. Each mirror carries the backend
 * member names and values (the drift spec and the dev-mode check compare them with the catalogue).
 */
export const ENUM_REGISTRY = {
	ActivityIndicatorStatus,
	ActivityStatus,
	AnimalTransporterStatus,
	AttachedDocumentType,
	ClsfActivityFeature,
	ClsfActivityRegistrationType,
	ClsfLegislationType,
	ClsfSanctionCreateSurface,
	ControlObjectType,
	DataSource,
	EvaluationRule,
	EventPriority,
	EventStatus,
	ImplementationOfDeficiencyPlan,
	IncidentAction,
	InspectionKind,
	InspectionProgressStep,
	InspectionSubType,
	InspectionType,
	MbmOriginRegion,
	Month,
	OrderStatus,
	QuestionnaireStatus,
	ReportInvestigationMethod,
	ReportRecommendation,
	ReportStatus,
	ReportType,
	RequirementResult,
	RequirementRiskWeight,
	RiskGroup,
	SanctionAppealCourtOutcome,
	SanctionAppealState,
	SanctionChildActionKind,
	SanctionFieldCode,
	SanctionStatus,
	SelfControlSystem,
	SgpAnimalSpecies,
	SgpCategory,
	SgpSpeciesDeclarationKind,
	SubjectLegalForm,
	SubjectStatus,
	SubjectType,
} as const;

/** A backend catalogue enum that has a TS mirror. */
export type EnumCatalogueName = keyof typeof ENUM_REGISTRY;

/** The TS enum type of a catalogue enum, e.g. `EnumValue<'ActivityStatus'>` is `ActivityStatus`. */
export type EnumValue<K extends EnumCatalogueName> = Extract<
	(typeof ENUM_REGISTRY)[K][keyof (typeof ENUM_REGISTRY)[K]],
	number
>;

/** The numeric members of a TS enum by member name; the reverse mappings (value → name) are left out. */
export function enumMembers(enumObject: object): Record<string, number> {
	return Object.fromEntries(
		Object.entries(enumObject).filter((entry): entry is [string, number] => typeof entry[1] === 'number')
	);
}
